import React, { createContext, useContext, useState, useCallback } from 'react';
import newProduct from '../services/newProduct';
import newProdutoPreco from '../services/newProdutoPreco';
import newEstoque from '../services/newEstoque';
import newProdutoDisponibilidade from '../services/newProdutoDisponibilidade';
import newComplemento from '../services/newComplemento';

interface ProductData {
  empresa_id: number;
  grupo_id: number;
  descricao: string;
  codigo?: string;
  gtin?: string;
  observacao?: string;
  preco: number;
  unidade_medida_id: number;
  imagem?: File | string;
  estoque_minimo?: number;
  estoque_atual?: number;
  tipo: number;
}

interface ITurnoType {
  inicio: number;
  fim: number;
}

interface ComplementoData {
  complemento_obrigatorio: boolean;
  descricao: string;
  qtd_min: number;
  qtd_max: number;
  tipo: number;
  empresa_id: number;
  index: number;
}

interface DisponibilidadeType {
  dias: {
    domingo: boolean;
    segunda: boolean;
    terca: boolean;
    quarta: boolean;
    quinta: boolean;
    sexta: boolean;
    sabado: boolean;
  };
  turnos: ITurnoType[];
}

interface ItemComplementoData {
  codigo_complemento?: string;
  descricao: string;
  tipo: number;
  preco: number;
  imagem?: File;
  produto_id?: number;
  index_complemento: number;
  qtd_min: number;
  qtd_max: number;
}

interface TurnoType {
  inicio: number;
  fim: number;
}

interface QtdTurnos {
  index: number;
}

interface DisponibilidadeData {
  dias: {
    domingo: boolean;
    segunda: boolean;
    terca: boolean;
    quarta: boolean;
    quinta: boolean;
    sexta: boolean;
    sabado: boolean;
  };
  turnos?: TurnoType[];
}

interface SimpleProductContextData {
  product: ProductData;
  complemento: ComplementoData[];
  itensComplemento: ItemComplementoData[];
  disponibilidade: DisponibilidadeData;
  filledData: boolean;
  filledData2: boolean;
  disponibilidadeReleased: boolean;
  itensComplementoReleased: boolean;
  complementoDrawerOpen: boolean;
  editComplementoDrawerWasClosed: boolean;
  disponibilidadeTabClicked: boolean;
  sempreDisponivel: boolean;
  drawerValue: number;
  qtdTurnos: QtdTurnos[];
  turnos: TurnoType[];
  setDrawerValue(value: number): void;
  createProduct(data: ProductData): void;
  createComplemento(data: ComplementoData): void;
  createItensComplemento(data: ItemComplementoData[]): void;
  createDisponibilidade(data: DisponibilidadeData): void;
  handleSetSempreDisponivel(value: boolean): void;
  updateComplemento(data: ComplementoData[]): void;
  updateItensComplemento(data: ItemComplementoData[]): void;
  handleSetQtdTurno(operacao: string, item_index?: number): void;
  handleSetTurnos(data: TurnoType[]): void;
  handleSetInitialQtdTurnos(data: QtdTurnos[]): void;
  handleSetFilledData(value: boolean): void;
  setFilledData2(value: boolean): void;
  handleSetDisponibilidadeReleased(value: boolean): void;
  handleSetItensComplementoReleased(value: boolean): void;
  handleSetComplementoDrawerOpen(value: boolean): void;
  handleSetEditComplementoDrawerWasClosed(value: boolean): void;
  handleSetDisponibilidadeTabClicked(value: boolean): void;

  handleAbortNewComplemento(): void;

  submit(): Promise<void>;
  cleanData(): void;
}

const SimpleProductContext = createContext<SimpleProductContextData>(
  {} as SimpleProductContextData,
);

const SimpleProductProvider: React.FC = ({ children }) => {
  const [product, setProduct] = useState<ProductData>(() => {
    return {} as ProductData;
  });
  const [disponibilidade, setDisponibilidade] = useState<DisponibilidadeData>(
    () => {
      return {} as DisponibilidadeData;
    },
  );

  const [complemento, setComplemento] = useState<ComplementoData[]>([]);
  const [itensComplemento, setItensComplemento] = useState<
    ItemComplementoData[]
  >([]);
  const [itensComplementoReleased, setItensComplementoReleased] = useState(
    false,
  );
  const [complementoDrawerOpen, setComplementoDrawerOpen] = useState(false);
  const [
    editComplementoDrawerWasClosed,
    setEditComplementoDrawerWasClosed,
  ] = useState(false);

  const [filledData, setFilledData] = useState(false);
  const [filledData2, setFilledData2] = useState(false);
  const [drawerValue, setDrawerValue] = useState(0);
  const [disponibilidadeReleased, setDisponibilidadeReleased] = useState(false);
  const [disponibilidadeTabClicked, setDisponibilidadeTabClicked] = useState(
    false,
  );
  const [qtdTurnos, setQtdTurnos] = useState<QtdTurnos[]>([]);
  const [turnos, setTurnos] = useState<TurnoType[]>([]);
  const [sempreDisponivel, setSempreDisponivel] = useState(true);

  const handleSetDisponibilidadeReleased = useCallback((value: boolean) => {
    setDisponibilidadeReleased(value);
  }, []);

  const handleSetSempreDisponivel = useCallback((value: boolean) => {
    setSempreDisponivel(value);
  }, []);

  const handleSetItensComplementoReleased = useCallback((value: boolean) => {
    setItensComplementoReleased(value);
  }, []);

  const handleSetComplementoDrawerOpen = useCallback((value: boolean) => {
    setComplementoDrawerOpen(value);
  }, []);

  const handleSetEditComplementoDrawerWasClosed = useCallback(
    (value: boolean) => {
      setEditComplementoDrawerWasClosed(value);
    },
    [],
  );

  const handleSetDisponibilidadeTabClicked = useCallback((value: boolean) => {
    setDisponibilidadeTabClicked(value);
  }, []);

  const handleSetFilledData = useCallback((value: boolean) => {
    setFilledData(value);
  }, []);

  const handleAbortNewComplemento = useCallback(() => {
    const complementos = [...complemento];
    complementos.splice(complemento.length - 1, 1);

    setComplemento([...complementos]);
  }, [complemento]);

  const createProduct = useCallback(
    ({
      empresa_id,
      grupo_id,
      descricao,
      codigo,
      gtin,
      observacao,
      preco,
      imagem,
      unidade_medida_id,
      estoque_atual,
      estoque_minimo,
      tipo,
    }: ProductData) => {
      setProduct({
        empresa_id,
        grupo_id,
        descricao,
        codigo,
        gtin,
        observacao,
        preco,
        imagem,
        unidade_medida_id,
        estoque_atual,
        estoque_minimo,
        tipo,
      });
    },
    [],
  );

  const createComplemento = useCallback(
    ({
      complemento_obrigatorio,
      descricao,
      qtd_min,
      qtd_max,
      tipo,
      empresa_id,
      index,
    }: ComplementoData) => {
      setComplemento((oldState) => [
        ...oldState,
        {
          complemento_obrigatorio,
          descricao,
          qtd_min,
          qtd_max,
          tipo,
          empresa_id,
          index,
        },
      ]);
    },
    [],
  );

  const handleSetQtdTurno = useCallback(
    (operacao: string, item_index?: number) => {
      const qtdAtual = [...qtdTurnos];
      const turnosAtuais = [...turnos];

      if (operacao === 'add') {
        if (qtdTurnos.length == 0) {
          setQtdTurnos([{ index: 0 }]);
          setTurnos([{ inicio: 0, fim: 0 }]);
          return;
        }

        qtdAtual.push({ index: qtdAtual[qtdTurnos.length - 1].index + 1 });
        turnosAtuais.push({ inicio: 0, fim: 0 });

        setQtdTurnos([...qtdAtual]);
        setTurnos([...turnosAtuais]);
      } else if (operacao === 'remove') {
        const qtdTurnosStoreds: QtdTurnos[] = qtdTurnos;
        const turnosStored = [...turnos];

        if (item_index !== undefined) {
          turnosStored.splice(item_index, 1);
        }
        qtdTurnosStoreds.pop();

        setQtdTurnos([...qtdTurnosStoreds]);
        setTurnos([...turnosStored]);
      }
    },
    [qtdTurnos, turnos],
  );

  const handleSetTurnos = useCallback((data: TurnoType[]) => {
    setTurnos(data);
  }, []);

  const updateComplemento = useCallback((data: ComplementoData[]) => {
    setComplemento([...data]);
  }, []);

  const updateItensComplemento = useCallback((data: ItemComplementoData[]) => {
    setItensComplemento([...data]);
  }, []);

  const createItensComplemento = useCallback((data: ItemComplementoData[]) => {
    setItensComplemento((oldState) => [...oldState, ...data]);
  }, []);

  const handleSetInitialQtdTurnos = useCallback((data: QtdTurnos[]) => {
    setQtdTurnos([...data]);
  }, []);

  const createDisponibilidade = useCallback(
    ({ dias, turnos: iturnos }: DisponibilidadeData) => {
      setDisponibilidade({
        dias,
        turnos: iturnos,
      });
    },
    [],
  );

  const cleanData = useCallback(() => {
    setProduct({} as ProductData);
    setComplemento([]);
    setDisponibilidade({} as DisponibilidadeData);
    setFilledData(false);
    setItensComplemento([]);
    setDisponibilidadeReleased(false);
  }, []);

  const submit = useCallback(async (): Promise<void> => {
    const {
      empresa_id,
      grupo_id,
      descricao,
      codigo,
      gtin,
      observacao,
      preco,
      imagem,
      unidade_medida_id,
      tipo,
      estoque_atual,
      estoque_minimo,
    } = product;

    let newImage: File | undefined;

    if (typeof imagem === 'string') {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/files/${imagem}`,
      );

      const blobFile = await response.blob();

      newImage = new File([blobFile], imagem.split('-')[1]);
    }

    const { produto_id, grupo_produto_id } = await newProduct({
      empresa_id,
      grupo_id,
      descricao,
      codigo,
      gtin,
      observacao,
      preco,
      imagem: newImage || imagem,
      unidade_medida_id,
      tipo,
    });

    await Promise.all([
      newProdutoPreco({ grupo_id, preco, produto_id }),
      newEstoque({ empresa_id, produto_id, estoque_atual, estoque_minimo }),
      newProdutoDisponibilidade({
        produto_id,
        role: disponibilidade,
      }),
    ]);

    if (complemento.length > 0) {
      await newComplemento(
        complemento,
        itensComplemento,
        grupo_produto_id,
        empresa_id,
      );
    }

    setFilledData2(false);
    setFilledData(false);
  }, [product, itensComplemento, complemento, disponibilidade]);

  return (
    <SimpleProductContext.Provider
      value={{
        product,
        turnos,
        qtdTurnos,
        complemento,
        itensComplemento,
        disponibilidade,
        filledData,
        disponibilidadeReleased,
        itensComplementoReleased,
        complementoDrawerOpen,
        editComplementoDrawerWasClosed,
        disponibilidadeTabClicked,
        sempreDisponivel,
        drawerValue,
        filledData2,
        setFilledData2,
        setDrawerValue,
        handleSetSempreDisponivel,
        createProduct,
        createComplemento,
        createItensComplemento,
        createDisponibilidade,
        updateComplemento,
        handleSetTurnos,
        handleSetInitialQtdTurnos,
        handleSetQtdTurno,
        updateItensComplemento,
        handleSetFilledData,
        handleSetDisponibilidadeReleased,
        handleSetItensComplementoReleased,
        handleSetComplementoDrawerOpen,
        handleSetEditComplementoDrawerWasClosed,
        handleSetDisponibilidadeTabClicked,
        handleAbortNewComplemento,
        submit,
        cleanData,
      }}
    >
      {children}
    </SimpleProductContext.Provider>
  );
};

function useSimpleProduct(): SimpleProductContextData {
  const context = useContext(SimpleProductContext);

  if (!context)
    throw new Error(
      'useSimpleProduct deve ser utilizado junto do SimpleProductProvider',
    );

  return context;
}

export { SimpleProductProvider, useSimpleProduct };
